import { Edge, Node, Position } from '@xyflow/react';
import type { TFunction } from 'i18next';
import type { StatusLabelResolver } from '../../../helpers/status-labels/status-labels.type';
import { primaryBase, redBase, secondaryBase } from '../../../styles/design-tokens';
import type { EventScope } from '../context/process.context.type';
import { ViewType } from '../process.type';
import type { EventGraph, EventGraphEdge, EventGraphNode } from './event-graph-client.type';
import { EventType } from './event-graph-client.type';
import type { EventNodeData } from './event-graph.type';

/**
 * Extracts the status from a node ID
 *
 * @param {string} nodeId - The ID of the node in format "eventType_status"
 * @returns {string} The status portion of the node ID
 */
function getStatusFromNodeId(nodeId: string): string {
  return nodeId.split('_')?.[1]?.toLowerCase() || '';
}

/**
 * Returns a human-readable label for an event graph node based on its type.
 *
 * @param {EventNodeData} node - The data of the event node.
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @param {StatusLabelResolver} [resolveStatusLabelForDisplay] - Maps the status parsed
 *   out of the node id to its source-cased label. Omit where board scope is
 *   unavailable and the raw lowercased status is acceptable.
 * @returns {string} A human-readable label describing the event.
 */
function getEventGraphNodeLabel(
  node: EventNodeData,
  t: TFunction,
  resolveStatusLabelForDisplay?: StatusLabelResolver,
): string {
  const maybeStatus = getStatusFromNodeId(node.id);
  switch (node.event_type) {
    case EventType.ASSIGNEE:
      return 'Assignee Changed';
    case EventType.COMPONENT:
      return 'Component Changed';
    case EventType.DESCRIPTION:
      return 'Description Changed';
    case EventType.DUEDATE:
      return 'Due Date Changed';
    case EventType.EPIC_LINK:
      return `${t('common.parent_task')} Changed`;
    case EventType.ESTIMATE:
      return `${t('common.estimation')} Changed`;
    case EventType.ISSUE_PARENT_ASSOCIATION:
      return `${t('common.parent_task')} Changed`;
    case EventType.ISSUETYPE:
      return `${t('common.type')} Changed`;
    case EventType.LABELS:
      return 'Labels Changed';
    case EventType.POINTS:
      return `${t('common.estimation')} Changed`;
    case EventType.PRIORITY:
      return 'Priority Changed';
    case EventType.REPORTER:
      return 'Reporter Changed';
    case EventType.RESOLUTION:
      return 'Resolution Changed';
    case EventType.SPRINT:
      return `${t('common.sprint')} Changed`;
    case EventType.STATUS:
      // `maybeStatus` is lowercased by `getStatusFromNodeId` so set-membership
      // comparisons elsewhere remain case-insensitive. The resolver restores
      // the board's real casing for display; without one the raw lowercased
      // value is shown rather than a guessed capitalization.
      return `${t('common.status.status')} Changed to '${
        resolveStatusLabelForDisplay ? resolveStatusLabelForDisplay(maybeStatus) : maybeStatus
      }'`;
    case EventType.SUMMARY:
      return `${t('common.name')} Changed`;
    case EventType.TIME_ESTIMATE:
      return 'Time Estimate Changed';
    case EventType.TIME_ORIGINAL_ESTIMATE:
      return 'Time Original Estimate Changed';
    default:
      return `${node.event_type} Changed`;
  }
}

/**
 * Determines the color and icon for an event graph node based on its type and status.
 *
 * @param {EventNodeData} node - The data of the event node.
 * @param {EventScope} eventScope - The scope of the event, including statuses.
 * @returns {{ color: string, icon: string }} An object containing the color and icon for the node.
 */
function getEventGraphNodeOptions(
  node: EventNodeData,
  eventScope: EventScope,
): {
  color: string;
  icon: string;
} {
  const maybeStatus = getStatusFromNodeId(node.id);
  if (node.event_type === EventType.STATUS) {
    if ((eventScope.statuses || []).map((status) => status.toLowerCase()).includes(maybeStatus)) {
      return {
        color: primaryBase,
        icon: 'login',
      };
    } else {
      return {
        color: redBase,
        icon: 'logout',
      };
    }
  } else {
    return {
      color: secondaryBase,
      icon: 'crop_square',
    };
  }
}

/**
 * Filters edges based on sequence and weight threshold.
 *
 * @param {EventGraph} eventGraph - The event graph to filter.
 * @param {number} filterLevel - The filter level (0.2 to 1.0).
 * @returns {{ filteredEdges: EventGraphEdge[], includedNodes: Set<string> }} An object containing the filtered edges and included nodes.
 */
function getFilteredEdgesAndNodes(
  eventGraph: EventGraph,
  filterLevel: number,
): { filteredEdges: EventGraphEdge[]; includedNodes: Set<string> } {
  // Group edges by source node sequence
  const edgesBySourceSequence: { [key: number]: typeof eventGraph.edges } = {};
  const nodeMap = new Map(eventGraph.nodes.map((node) => [node.id, node]));

  // Initialize and group edges by source node sequence
  eventGraph.edges.forEach((edge) => {
    const sourceNode = nodeMap.get(edge.source);
    if (sourceNode) {
      const sequence = sourceNode.sequence;
      if (!edgesBySourceSequence[sequence]) {
        edgesBySourceSequence[sequence] = [];
      }
      edgesBySourceSequence[sequence].push(edge);
    }
  });

  // Track nodes that should be included for each sequence
  // We start with sequence 0 nodes and only include nodes that are connected by filtered edges
  const includedNodes = new Set<string>();

  // For sequence 0, include all nodes initially
  eventGraph.nodes
    .filter((node) => node.sequence === 0)
    .forEach((node) => {
      includedNodes.add(node.id);
    });

  // Filter edges for each sequence and track connected nodes
  const filteredEdges = [];

  // Process each sequence in order
  const maxSequence = Math.max(...Object.keys(edgesBySourceSequence).map(Number));
  for (let sequence = 0; sequence <= maxSequence; sequence++) {
    const sequenceEdges = edgesBySourceSequence[sequence] || [];

    // Only consider edges from nodes that were included from previous sequence
    const relevantEdges = sequenceEdges.filter((edge) => includedNodes.has(edge.source));

    // Sort by weight (descending)
    relevantEdges.sort((a, b) => b.weight - a.weight);

    // Take top x% of edges as determined by filterLevel (where filterLevel is 0.2 to 1.0)
    const edgesToKeep = Math.max(1, Math.ceil(relevantEdges.length * filterLevel));
    const keptEdges = relevantEdges.slice(0, edgesToKeep);

    // Add these edges to the filtered set
    filteredEdges.push(...keptEdges);

    // Track nodes connected by these edges for the next sequence
    keptEdges.forEach((edge) => {
      includedNodes.add(edge.target);
    });
  }

  // Create a set of active nodes (those that have edges after filtering)
  const activeNodes = new Set<string>();

  // Add all nodes that are either a source or target in filtered edges
  filteredEdges.forEach((edge) => {
    activeNodes.add(edge.source);
    activeNodes.add(edge.target);
  });

  // For sequence 0, if no edges are connected to a node, remove it from included nodes
  if (filterLevel < 1) {
    const sequence0Nodes = eventGraph.nodes.filter((node) => node.sequence === 0);
    sequence0Nodes.forEach((node) => {
      // If the node is not active (has no edges), remove it from includedNodes
      if (!activeNodes.has(node.id)) {
        includedNodes.delete(node.id);
      }
    });
  }

  return { filteredEdges, includedNodes };
}

/**
 * Calculates a backwards edge for the event graph visualization.
 *
 * This function handles the special case of edges that traverse backwards in the graph
 * (typically from a higher sequence to sequence 0). It creates a new edge label node
 * positioned appropriately and generates the necessary edges to connect the source and target.
 *
 * @param {EventGraphEdge} edge - The edge data containing source, target, and metrics (weight, task_count, etc.)
 * @param {EventGraphNode} sourceNode - The source node of the edge
 * @param {EventGraphNode} targetNode - The target node of the edge
 * @param {Node<EventNodeData>[]} rfNodes - The current array of React Flow nodes in the graph
 *
 * @returns {Object} An object containing:
 *   - newEdgeLabelNode: A new node to display the edge label and metrics
 *   - newEdges: Array of edges to connect the source and target through the label node
 */
function calculateBackwardsEdge(
  edge: EventGraphEdge,
  sourceNode: EventGraphNode,
  targetNode: EventGraphNode,
  rfNodes: Node<EventNodeData>[],
  viewType: ViewType,
): { newEdgeLabelNode: Node | null; newEdges: Edge[] } {
  const sourceNodeData = rfNodes.find((node) => node.id === sourceNode.id);
  const targetNodeData = rfNodes.find((node) => node.id === targetNode.id);

  if (!sourceNodeData || !targetNodeData) {
    return { newEdgeLabelNode: null, newEdges: [] };
  }

  const Y = sourceNodeData.position.y - targetNodeData.position.y - 100;

  const isExitNode = sourceNodeData.data?.isExitNode ?? false;

  const Xoffset = 250;
  let X: number;

  const allEventNodeXs = [
    ...rfNodes
      .filter((node) => node.type === 'eventNode')
      .map((node) => {
        const nodeWidth = node.measured?.width || node.width || 200;
        return node.position.x + nodeWidth / 2;
      }),
  ];

  const backWardsNodesOnSameY = rfNodes.filter(
    (node) =>
      node.type === 'edgeLabel' &&
      node.position.y === Y &&
      ((isExitNode && node.position.x < 0) || (!isExitNode && node.position.x > 0)),
  );

  if (isExitNode) {
    if (backWardsNodesOnSameY.length > 0) {
      X =
        Math.min(
          ...backWardsNodesOnSameY.map((node) => {
            const nodeWidth = node.measured?.width || node.width || 200;
            return node.position.x - nodeWidth / 2;
          }),
        ) - Xoffset;
    } else {
      X = Math.min(...allEventNodeXs) - Xoffset;
    }
  } else {
    if (backWardsNodesOnSameY.length > 0) {
      X =
        Math.max(
          ...backWardsNodesOnSameY.map((node) => {
            const nodeWidth = node.measured?.width || node.width || 200;
            return node.position.x + nodeWidth / 2;
          }),
        ) + Xoffset;
    } else {
      X = Math.max(...allEventNodeXs) + Xoffset;
    }
  }

  const isRightGraph = sourceNodeData.data?.isRightGraph ?? false;

  const newEdgeLabelNode = {
    id: `${edge.source}-${edge.target}-edge-label`,
    type: 'edgeLabel',
    position: {
      x: X,
      y: Y,
    },
    data: {
      weight: edge.weight,
      task_count: edge.task_count,
      percentage: edge.percentage,
      cycle_time: edge.cycle_time,
      cost: edge.cost,
      edgeId: `${edge.source},${edge.target}`,
      isRightGraph,
      viewType: viewType,
    },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
  };

  const newEdges: Edge[] = [
    {
      id: `${edge.source}-${newEdgeLabelNode.id}`,
      source: edge.source,
      target: newEdgeLabelNode.id,
      sourceHandle: `${edge.source}-source`,
      targetHandle: `${newEdgeLabelNode.id}-target`,
      type: 'backwardsEdge',
      data: {
        isRightGraph,
      },
      animated: true, // Add animation for better visibility
    },
    {
      id: `${newEdgeLabelNode.id}-${edge.target}`,
      source: newEdgeLabelNode.id,
      target: edge.target,
      sourceHandle: `${newEdgeLabelNode.id}-source`,
      targetHandle: `${edge.target}-target`,
      type: 'backwardsEdge',
      data: {
        isRightGraph,
      },
      animated: true, // Add animation for better visibility
    },
  ];

  return { newEdgeLabelNode, newEdges };
}

export { calculateBackwardsEdge, getEventGraphNodeLabel, getEventGraphNodeOptions, getFilteredEdgesAndNodes };
